import { useFetchData } from "@/lib/GraphQLCodegen/fetcher";
import { supplierTripByOfficesQuery } from "@/app/SupplierTrips/GraphQL/supplierTripRecord";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import {
  TripDefaultChangeItem,
  UpdateTripDefaultsDialog,
} from "@/app/Relocations/Components/UpdateTripDefaultsDialog";
import { UpdateSupplierTripInput } from "@/gql/graphql";
import { localStorageKeys } from "@/lib/utils";
import { ResultOf } from "@graphql-typed-document-node/core";
import { useTranslations } from "use-intl";

type TripDefaultValues = Pick<
  UpdateSupplierTripInput,
  | "hire_units_allowed"
  | "extra_hire_units_allowed"
  | "minimum_hire_units"
  | "distance_allowed"
  | "is_ferry_required"
>;

type TripDefaultField = {
  key: keyof TripDefaultValues;
  label: string;
  format: (value: unknown) => string;
};

type TripSupplier = NonNullable<
  ResultOf<typeof supplierTripByOfficesQuery>["supplierTripByOffices"]
>["supplier"];

function tripDefaultFields(
  t: ReturnType<typeof useTranslations<"relocation">>,
  supplier: TripSupplier,
): TripDefaultField[] {
  function formatHireUnits(value: unknown) {
    const unit =
      value === 1
        ? t(`hire_unit_type.${supplier.hire_unit_type}`)
        : t(`hire_unit_type_plural.${supplier.hire_unit_type}`);

    return `${value} ${unit}`;
  }

  return [
    {
      key: "hire_units_allowed",
      label: t("form.relocationHire"),
      format: formatHireUnits,
    },
    {
      key: "extra_hire_units_allowed",
      label: t("form.extraHire"),
      format: formatHireUnits,
    },
    {
      key: "minimum_hire_units",
      label: t("form.minimumHire"),
      format: formatHireUnits,
    },
    {
      key: "distance_allowed",
      label: t(`distance_allowed_label.${supplier.measurement}`),
      format: (value) =>
        value === null || value === undefined
          ? t("summaryCard.unlimited")
          : String(value),
    },
    {
      key: "is_ferry_required",
      label: t("form.requireFerryDate"),
      format: (value) => (value ? "Yes" : "No"),
    },
  ];
}

type OfferTripDefaultsUpdateArgs = {
  officeAId?: string | null;
  officeBId?: string | null;
  values: TripDefaultValues;
  /**
   * When true, resolves only once the dialog is dismissed (use before
   * navigating away). When false, resolves as soon as the dialog is shown so
   * the caller's form can return to its view state behind it.
   */
  awaitDialog?: boolean;
};

/**
 * After a relocation is saved, compares the trip-default fields
 * against the route's saved SupplierTrip. If the supplier deviated, offers to
 * mirror the chosen values onto the SupplierTrip defaults.
 */
export function useOfferTripDefaultsUpdate(): (
  args: OfferTripDefaultsUpdateArgs,
) => Promise<void> {
  const t = useTranslations("relocation");
  const fetchTrip = useFetchData(supplierTripByOfficesQuery);
  const { open, openAsPromise } = useDialog(UpdateTripDefaultsDialog);

  return async ({ officeAId, officeBId, values, awaitDialog = false }) => {
    if (
      !officeAId ||
      !officeBId ||
      localStorage.getItem(localStorageKeys.SKIP_TRIP_DEFAULTS_PROMPT)
    ) {
      return;
    }

    const { supplierTripByOffices: trip } = await fetchTrip({
      officeAId,
      officeBId,
    });

    if (!trip) {
      return;
    }

    const items: TripDefaultChangeItem[] = [];

    for (const field of tripDefaultFields(t, trip.supplier)) {
      const next = values[field.key];
      const current = trip[field.key];

      if (next === null || next === undefined || next === current) {
        continue;
      }

      items.push({
        id: `field:${field.key}`,
        label: field.label,
        from: field.format(current),
        to: field.format(next),
        field: { key: field.key, value: next },
      });
    }

    if (items.length === 0) {
      return;
    }

    if (awaitDialog) {
      // Dismissing / "Keep defaults" rejects — not an error for the caller.
      await openAsPromise({ tripId: trip.id, items }).catch(() => {});
      return;
    }

    open({ tripId: trip.id, items });
  };
}
